import { environment } from '@/config/environment'
import { iamAuth } from '@/lib/firebase'

/**
 * Mirror of iOS `CoachStreamClient.swift`.
 *
 * Backend (`backend/functions/src/routes/coach.ts`) emits SSE frames
 * shaped as `data: {"type":"chunk","content":"..."}` plus a final
 * `{"type":"complete"}` and (legacy) `[DONE]` terminator. We decode
 * the same shape here and yield chunks via async iteration.
 *
 * Bearer auth: ID token from the IAM Firebase app (same path iOS
 * uses). No cookies, no CSRF surface.
 */

export interface CoachImageAttachment {
  mediaType: 'image/jpeg' | 'image/png' | 'image/gif' | 'image/webp'
  dataBase64: string
  filename?: string
}

interface CoachRequest {
  message: string
  attachments?: CoachImageAttachment[]
}

export async function* streamCoach(
  request: CoachRequest,
  signal?: AbortSignal,
): AsyncGenerator<string> {
  const user = iamAuth.currentUser
  if (!user) throw new Error('Not signed in')
  const token = await user.getIdToken()

  const res = await fetch(`${environment.apiBaseURL}/coach/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(request),
    signal,
  })

  if (!res.ok || !res.body) {
    let detail: string | null = null
    try {
      const body = await res.json()
      detail = typeof body?.error === 'string' ? body.error : null
    } catch {
      // body wasn't JSON; that's fine.
    }
    throw new Error(detail ?? `Coach HTTP ${res.status}`)
  }

  const decoder = new TextDecoder()
  const reader = res.body.getReader()
  let buffer = ''

  while (true) {
    const { value, done } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })

    // SSE frames are separated by a blank line. Split, leave a
    // trailing partial frame in the buffer for the next read.
    const frames = buffer.split('\n\n')
    buffer = frames.pop() ?? ''
    for (const frame of frames) {
      const trimmed = frame.trim()
      if (!trimmed.startsWith('data:')) continue
      const payload = trimmed.slice(5).trim()
      if (payload === '[DONE]') return
      try {
        const event = JSON.parse(payload) as {
          type?: string
          content?: string
          value?: string
          error?: string
        }
        const text = event.content ?? event.value
        if (event.type === 'chunk' || event.type === 'text') {
          if (text) yield text
        } else if (event.type === 'complete' || event.type === 'done') {
          return
        } else if (event.type === 'error') {
          throw new Error(event.error ?? 'Coach stream error')
        }
      } catch (err) {
        // If JSON parse fails, treat the raw payload as a chunk —
        // forward-compat with the legacy text-only shape.
        if (err instanceof SyntaxError) yield payload
        else throw err
      }
    }
  }
}
